import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { InstallmentSeries } from "@/hooks/useInstallments";

interface InstallmentProgressProps {
  series: InstallmentSeries;
  showPercentage?: boolean;
  size?: "sm" | "md" | "lg";
}

const sizeStyles = {
  sm: {
    bar: "h-1.5",
    text: "text-xs"
  },
  md: {
    bar: "h-2",
    text: "text-sm"
  },
  lg: {
    bar: "h-3",
    text: "text-base"
  }
};

export function InstallmentProgress({
  series,
  showPercentage = true,
  size = "md"
}: InstallmentProgressProps) {
  const styles = sizeStyles[size];
  const { paid_count, total_installments, progress_percentage, status } = series;

  const getStatusColor = () => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "cancelled":
        return "bg-gray-400";
      case "active":
      default:
        return "bg-primary";
    }
  };

  const getStatusBadge = () => {
    switch (status) {
      case "completed":
        return <Badge variant="outline" className="text-green-600 border-green-600">Concluído</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="text-foreground border-gray-600">Cancelado</Badge>;
      case "active":
      default:
        return null;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className={cn("font-medium text-muted-foreground", styles.text)}>
          {paid_count}/{total_installments} {total_installments === 1 ? "parcela" : "parcelas"} pagas
        </span>
        <div className="flex items-center gap-2">
          {showPercentage && (
            <span className={cn("font-semibold", styles.text)}>
              {Math.round(progress_percentage)}%
            </span>
          )}
          {getStatusBadge()}
        </div>
      </div>

      {/* Progress Bar */}
      <div className={cn("w-full bg-secondary rounded-full overflow-hidden", styles.bar)}>
        <div
          className={cn("h-full rounded-full transition-all duration-500 ease-out", getStatusColor())}
          style={{ width: `${progress_percentage}%` }}
          role="progressbar"
          aria-valuenow={progress_percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${paid_count} de ${total_installments} parcelas pagas`}
        />
      </div>
    </div>
  );
}
